import { useEffect, useState, useRef } from 'react';
import api from '../api';

/**
 * Snack-bar buyurtmalarini global kuzatadi:
 * - Har 5 sek pending-orders polling
 * - Yangi pending order kelganda audio + modal
 * - Qabul qilinmagan order har 60 sek qayta audio bilan eslatadi
 * - Qabul qilingandan 5 minut o'tib "Yetkazildimi?" modal chiqaradi
 * - "Yo'q" tanlansa 2 minutdan keyin yana so'raydi
 */
export default function OrderAlerts() {
  const [labs, setLabs] = useState([]);
  const [labId, setLabId] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [helpRequests, setHelpRequests] = useState([]);
  const [activeModal, setActiveModal] = useState(null); // 'new' | 'delivery' | 'help'
  const [activeOrder, setActiveOrder] = useState(null);
  const [activeHelp, setActiveHelp] = useState(null);
  const lastSeenIds = useRef(new Set()); // pending bo'lib ko'rilgan order id lar
  const lastSeenHelpIds = useRef(new Set());
  const lastReminded = useRef({}); // { orderId: timestamp }
  const deliveryTimers = useRef({}); // { orderId: timeoutId }
  const audioCtxRef = useRef(null);

  // Audio (Web Audio API beep — fayl talab qilmaydi)
  const beep = (count = 2) => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      for (let i = 0; i < count; i++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.25);
        gain.gain.linearRampToValueAtTime(0.2, ctx.currentTime + i * 0.25 + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + i * 0.25 + 0.18);
        osc.start(ctx.currentTime + i * 0.25);
        osc.stop(ctx.currentTime + i * 0.25 + 0.2);
      }
    } catch {}
  };

  // /all-pending darhol owner ning BARCHA lablari uchun pending orderlarni qaytaradi
  // (frontend lab_id tanlash shart emas)
  useEffect(() => {
    setLabId('all'); // marker — polling boshlanishi uchun
  }, []);

  // Polling — 5 sekund
  useEffect(() => {
    if (!labId) return;
    console.log('[OrderAlerts] polling boshlandi, lab_id=', labId);
    let stopped = false;

    const tick = async () => {
      try {
        const [oR, hR] = await Promise.all([
          api.get(`/snack/all-pending`).catch(() => ({ data: [] })),
          api.get(`/sessions/help-requests`).catch(() => ({ data: [] })),
        ]);
        if (stopped) return;
        const orders = oR.data || [];
        const helps = hR.data || [];
        setHelpRequests(helps);
        // Yangi help so'rov uchun audio + modal
        for (const h of helps) {
          if (!lastSeenHelpIds.current.has(h.id)) {
            lastSeenHelpIds.current.add(h.id);
            beep(3);
            if (!activeModal) {
              setActiveHelp(h);
              setActiveModal('help');
            }
          }
        }
        setPendingOrders(orders);

        // Yangi pending orderlar
        const now = Date.now();
        for (const o of orders) {
          if (o.acceptance_status === 'pending') {
            if (!lastSeenIds.current.has(o.id)) {
              lastSeenIds.current.add(o.id);
              beep(2);
              if (!activeModal) showOrder(o);
            } else {
              // 60 sek o'tdi mi — qayta eslatish
              const last = lastReminded.current[o.id] || 0;
              if (now - last > 60000) {
                beep(3);
                lastReminded.current[o.id] = now;
              }
            }
          }
          // Qabul qilingan orderlar uchun — 5 minutdan keyin yetkazildimi tekshirish
          if (o.acceptance_status === 'accepted' && !deliveryTimers.current[o.id]) {
            const accepted = new Date(o.accepted_at).getTime();
            const due = accepted + 5 * 60 * 1000 - now;
            if (due <= 0) {
              triggerDeliveryCheck(o);
            } else {
              deliveryTimers.current[o.id] = setTimeout(() => triggerDeliveryCheck(o), due);
            }
          }
        }
      } catch {}
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { stopped = true; clearInterval(id); };
  }, [labId, activeModal]);

  function showOrder(order) {
    setActiveOrder(order);
    setActiveModal('new');
  }

  function triggerDeliveryCheck(order) {
    delete deliveryTimers.current[order.id];
    beep(2);
    setActiveOrder(order);
    setActiveModal('delivery');
  }

  async function acceptOrder() {
    if (!activeOrder) return;
    try {
      await api.post(`/snack/orders/${activeOrder.id}/accept`);
      setActiveModal(null);
      setActiveOrder(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    }
  }

  async function resolveHelp() {
    if (!activeHelp) return;
    try {
      await api.post(`/sessions/help-requests/${activeHelp.id}/resolve`);
      setActiveModal(null);
      setActiveHelp(null);
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    }
  }

  async function deliverChoice(delivered) {
    if (!activeOrder) return;
    try {
      await api.post(`/snack/orders/${activeOrder.id}/deliver-check`, { delivered });
      setActiveModal(null);
      setActiveOrder(null);
      if (!delivered) {
        // 2 minutdan keyin yana
        deliveryTimers.current[activeOrder.id] = setTimeout(
          () => triggerDeliveryCheck(activeOrder),
          2 * 60 * 1000
        );
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    }
  }

  function closeModal() {
    setActiveModal(null);
    setActiveOrder(null);
  }

  const pendingCount = pendingOrders.filter(o => o.acceptance_status === 'pending').length;
  const helpCount = helpRequests.length;

  return (
    <>
      {/* Pending zakaz tugmasi */}
      {pendingCount > 0 && (
        <button
          onClick={() => {
            const first = pendingOrders.find(o => o.acceptance_status === 'pending');
            if (first) showOrder(first);
          }}
          style={{
            position: 'fixed', top: 20, right: 24, zIndex: 1500,
            background: 'linear-gradient(135deg, #ef4444, #b91c1c)',
            border: 'none', color: 'white', padding: '14px 20px', borderRadius: 999,
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 10px 30px rgba(239,68,68,0.5)', animation: 'oaPulse 1.4s infinite',
          }}>
          🔔 {pendingCount} yangi zakaz
        </button>
      )}
      {/* Yordam so'rovi tugmasi (zakaz tugmasi tagida) */}
      {helpCount > 0 && (
        <button
          onClick={() => { setActiveHelp(helpRequests[0]); setActiveModal('help'); }}
          style={{
            position: 'fixed', top: pendingCount > 0 ? 75 : 20, right: 24, zIndex: 1500,
            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
            border: 'none', color: 'white', padding: '14px 20px', borderRadius: 999,
            fontSize: 14, fontWeight: 700, cursor: 'pointer',
            boxShadow: '0 10px 30px rgba(245,158,11,0.5)', animation: 'oaPulse 1.4s infinite',
          }}>
          🆘 {helpCount} yordam so'rovi
        </button>
      )}
      <style>{`@keyframes oaPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.06); } }`}</style>

      {/* Help modal */}
      {activeModal === 'help' && activeHelp && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <div style={{
            width: 'min(480px, 92vw)', background: 'var(--card)',
            border: '2px solid #f59e0b', borderRadius: 16, padding: 26, position: 'relative',
          }}>
            <button onClick={() => { setActiveModal(null); setActiveHelp(null); }} style={{
              position: 'absolute', top: 12, right: 12, background: 'transparent', border: 'none',
              color: 'var(--text3)', fontSize: 20, cursor: 'pointer',
            }}>✕</button>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6 }}>🆘 Yordam so'ralmoqda!</div>
            <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 14 }}>
              <b>PC #{activeHelp.computer_number}</b> · {activeHelp.lab_name || ''}
              {activeHelp.user_name && <> · {activeHelp.user_name}</>}
            </div>
            {activeHelp.message ? (
              <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap' }}>
                {activeHelp.message}
              </div>
            ) : (
              <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>
                Mijoz qo'shimcha izoh qoldirmadi
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 14 }}>
              {new Date(activeHelp.created_at).toLocaleString('uz-UZ', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
            </div>
            <button onClick={resolveHelp} style={{
              width: '100%', background: 'linear-gradient(135deg, var(--green), #059669)',
              border: 'none', color: '#fff', padding: '14px', borderRadius: 10,
              fontSize: 15, fontWeight: 800, cursor: 'pointer',
              boxShadow: '0 8px 24px rgba(0,255,136,0.3)',
            }}>✓ Hal qilindi</button>
          </div>
        </div>
      )}

      {/* Modal */}
      {activeModal && activeModal !== 'help' && activeOrder && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <div style={{
            width: 'min(480px, 92vw)', background: 'var(--card)',
            border: `2px solid ${activeModal === 'delivery' ? 'var(--orange)' : 'var(--red)'}`,
            borderRadius: 16, padding: 26, position: 'relative',
          }}>
            <button onClick={closeModal} style={{
              position: 'absolute', top: 12, right: 12, background: 'transparent', border: 'none',
              color: 'var(--text3)', fontSize: 20, cursor: 'pointer',
            }}>✕</button>

            {activeModal === 'new' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>🔔 Yangi buyurtma!</div>
                  {activeOrder.pre_order ? (
                    <span style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 800,
                      background: '#9B5CFF20', color: '#9B5CFF', border: '1px solid #9B5CFF80',
                      letterSpacing: 0.5,
                    }}>🕘 BRONDAN</span>
                  ) : (
                    <span style={{
                      padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 800,
                      background: '#27E0FF20', color: '#27E0FF', border: '1px solid #27E0FF80',
                      letterSpacing: 0.5,
                    }}>🪑 ZALDAN</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
                  PC #{activeOrder.computer_number || '?'} · {activeOrder.user_name || 'Mijoz'} ({activeOrder.user_phone || ''})
                </div>
                {activeOrder.pre_order && activeOrder.booking_scheduled_at && (
                  <div style={{ fontSize: 12, color: '#9B5CFF', marginBottom: 14, padding: '6px 10px',
                                background: '#9B5CFF15', borderRadius: 6 }}>
                    📅 Bron vaqti: {new Date(activeOrder.booking_scheduled_at).toLocaleString('uz-UZ', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                    {' — mijoz taxminan 30 daq ichida keladi'}
                  </div>
                )}

                <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
                  {activeOrder.items.map((it, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: i < activeOrder.items.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <span><b>{it.qty}×</b> {it.product_name}</span>
                      <span style={{ color: 'var(--cyan)' }}>{Number(it.subtotal).toLocaleString()} so'm</span>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 13 }}>
                    <span style={{ color: 'var(--text2)' }}>To'lov: </span>
                    <span style={{ color: activeOrder.payment_type === 'cash' ? 'var(--orange)' : activeOrder.payment_type === 'bonus' ? 'var(--purple, #a855f7)' : 'var(--green)', fontWeight: 700 }}>
                      {activeOrder.payment_type === 'cash' ? '💵 NAQD (yetkazganda)' :
                       activeOrder.payment_type === 'card_click' ? '💳 Click ✓' :
                       activeOrder.payment_type === 'card_payme' ? '💳 Payme ✓' :
                       activeOrder.payment_type === 'bonus' ? '🎁 Sovg\'a (bepul)' : '💰 Balansdan ✓'}
                    </span>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--green)' }}>
                    {Number(activeOrder.total).toLocaleString()} so'm
                  </div>
                </div>

                <button onClick={acceptOrder} style={{
                  width: '100%', background: 'linear-gradient(135deg, var(--green), #059669)',
                  border: 'none', color: '#fff', padding: '14px', borderRadius: 10,
                  fontSize: 15, fontWeight: 800, cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(0,255,136,0.3)',
                }}>✓ Qabul qilish</button>
                <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', marginTop: 10 }}>
                  ⏱ Qabul qilinmasa 1 daqiqada yana eslatamiz
                </div>
              </>
            )}

            {activeModal === 'delivery' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 22, fontWeight: 800 }}>📦 Yetkazib berildimi?</div>
                  {activeOrder.pre_order ? (
                    <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 800,
                      background: '#9B5CFF20', color: '#9B5CFF', border: '1px solid #9B5CFF80', letterSpacing: 0.5 }}>🕘 BRONDAN</span>
                  ) : (
                    <span style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 800,
                      background: '#27E0FF20', color: '#27E0FF', border: '1px solid #27E0FF80', letterSpacing: 0.5 }}>🪑 ZALDAN</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 18 }}>
                  PC #{activeOrder.computer_number || '?'} · {activeOrder.user_name || 'Mijoz'}
                </div>
                <div style={{ background: 'var(--bg2)', borderRadius: 10, padding: 14, marginBottom: 18 }}>
                  {activeOrder.items.map((it, i) => (
                    <div key={i}>{it.qty}× {it.product_name}</div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => deliverChoice(false)} style={{
                    flex: 1, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--red)',
                    color: 'var(--red)', padding: 12, borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  }}>✕ Yo'q (2 daqiqada yana so'rang)</button>
                  <button onClick={() => deliverChoice(true)} style={{
                    flex: 1, background: 'linear-gradient(135deg, var(--green), #059669)',
                    border: 'none', color: '#fff', padding: 12, borderRadius: 10, fontSize: 14, fontWeight: 800, cursor: 'pointer',
                  }}>✓ Ha, yetkazildi</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
